/**
 * Sometimes Hint Row — kind badge (regular/dual/entrance) + check combobox + item drop slot(s).
 * - Click the kind badge to cycle through types.
 * - Dual kind shows two item drop slots.
 * - Entrance kind tints the row (sky blue).
 */
import { ArrowRight } from 'lucide-react';
import { useTracker, type OotItemDto, type SometimesKind } from '@/contexts/tracker-context';
import { ItemDropSlot } from './item-drop-slot';
import { CheckAutocomplete, type CheckAutocompleteValue } from './check-autocomplete';
import type { OotItemData } from './data/oot-items-data';

interface SometimesRowProps {
  id: number;
}

const KIND_CYCLE: SometimesKind[] = ['regular', 'dual', 'entrance'];

const KIND_BADGE: Record<SometimesKind, { label: string; color: string; rowTint: string }> = {
  regular: {
    label: 'S',
    color: 'text-gray-300 bg-gray-700/60 border border-gray-600/40',
    rowTint: 'bg-gray-800/50',
  },
  dual: {
    label: 'D',
    color: 'text-purple-300 bg-purple-900/60 border border-purple-500/40',
    rowTint: 'bg-purple-950/30',
  },
  entrance: {
    label: 'E',
    color: 'text-sky-300 bg-sky-900/60 border border-sky-500/40',
    rowTint: 'bg-sky-950/30',
  },
};

export function SometimesRow({ id }: SometimesRowProps) {
  const { state, dispatch } = useTracker();

  const hint = state.sometimesHints[id] ?? {
    location: null,
    items: [],
    goal: null,
    count: 0,
    player: null,
    text: '',
    checkId: null,
    kind: 'regular' as SometimesKind,
  };
  const kind: SometimesKind = hint.kind ?? 'regular';
  const badge = KIND_BADGE[kind];

  const checkValue: CheckAutocompleteValue = {
    text: hint.text ?? '',
    checkId: hint.checkId ?? null,
  };

  const handleCheckChange = (next: CheckAutocompleteValue) => {
    dispatch({
      type: 'SET_SOMETIMES',
      id,
      hint: { ...hint, kind, text: next.text, checkId: next.checkId },
    });
  };

  const handleItemChange = (index: number, item: OotItemData | null) => {
    const items = [...hint.items];
    while (items.length <= index) items.push(null as unknown as OotItemDto);
    items[index] = item as unknown as OotItemDto;
    dispatch({
      type: 'SET_SOMETIMES',
      id,
      hint: { ...hint, kind, items },
    });
  };

  const cycleKind = () => {
    const next = KIND_CYCLE[(KIND_CYCLE.indexOf(kind) + 1) % KIND_CYCLE.length];
    dispatch({
      type: 'SET_SOMETIMES',
      id,
      hint: { ...hint, kind: next },
    });
  };

  return (
    <div className={`flex items-center gap-1 py-0.5 px-2 rounded-md ${badge.rowTint}`}>
      <button
        onClick={cycleKind}
        title={`Kind: ${kind} (click to cycle)`}
        className={`text-[9px] font-bold leading-none px-1 py-0.5 rounded flex-shrink-0 ${badge.color} hover:brightness-125`}
      >
        {badge.label}
      </button>

      <CheckAutocomplete
        value={checkValue}
        onChange={handleCheckChange}
        placeholder={kind === 'dual' ? 'Dual Hint' : kind === 'entrance' ? 'Entrance Hint' : 'Sometimes'}
      />

      <ArrowRight size={16} className="text-gray-500 flex-shrink-0" />

      <ItemDropSlot
        item={hint.items[0] as unknown as OotItemData | null}
        onItemChange={(item) => handleItemChange(0, item)}
      />

      {kind === 'dual' && (
        <ItemDropSlot
          item={hint.items[1] as unknown as OotItemData | null}
          onItemChange={(item) => handleItemChange(1, item)}
        />
      )}
    </div>
  );
}
